import { MEMBERS, ageFromDob } from "../wizardUtils";

function Row({ label, value, testId }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between py-3 gap-1 border-b border-subtle last:border-0">
      <span className="text-xs uppercase tracking-widest text-muted-brand">
        {label}
      </span>
      <span
        className="text-base text-primary-brand font-medium sm:text-right break-words"
        data-testid={testId}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-surface border border-subtle rounded-2xl p-6 mb-6">
      <h3 className="text-lg font-medium mb-3">{title}</h3>
      {children}
    </div>
  );
}

function maskAccount(acc) {
  if (!acc) return "";
  return acc.length > 4 ? "•".repeat(acc.length - 4) + acc.slice(-4) : acc;
}

export default function Review({ state }) {
  const membersLabel = state.selectedMembers
    .map((m) => MEMBERS.find((x) => x.key === m)?.label || m)
    .join(", ");
  const p = state.proposer;

  return (
    <div data-testid="wizard-step-review">
      <h2 className="text-3xl sm:text-4xl font-medium tracking-tight mb-2">
        Review your details
      </h2>
      <p className="text-secondary-brand mb-8 leading-relaxed">
        Check everything before we move on to document collection.
      </p>

      <Section title="Proposer">
        <Row label="Name" value={p.name} testId="review-p-name" />
        <Row label="Mobile" value={p.mobile} testId="review-p-mobile" />
        {p.alternateMobile && (
          <Row label="Alt Mobile" value={p.alternateMobile} testId="review-p-altmobile" />
        )}
        <Row label="Email" value={p.email} testId="review-p-email" />
        <Row
          label="Address"
          value={`${p.address}${p.city ? ", " + p.city : ""} — ${p.pincode}`}
          testId="review-p-address"
        />
        <Row label="Occupation" value={p.occupation} testId="review-p-occupation" />
        <Row label="Qualification" value={p.qualification} testId="review-p-qualification" />
        <Row label="Annual Income" value={p.annualIncome} testId="review-p-income" />
      </Section>

      <Section title="Bank">
        <Row label="Account" value={maskAccount(state.bank.accountNumber)} testId="review-bank-account" />
        <Row label="IFSC" value={state.bank.ifscCode} testId="review-bank-ifsc" />
      </Section>

      <Section title={`Insured (${state.selectedMembers.length})`}>
        <Row label="Members" value={membersLabel} testId="review-members" />
        <div className="mt-4 grid grid-cols-1 gap-3">
          {state.selectedMembers.map((m) => {
            const d = state.insured[m] || {};
            const age = ageFromDob(d.dob);
            const label = MEMBERS.find((x) => x.key === m)?.label || m;
            const pe = d.preExisting || {};
            const ls = d.lifestyle || {};
            return (
              <div
                key={m}
                className="rounded-xl bg-base p-4"
                data-testid={`review-insured-${m}`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <p className="text-sm font-semibold">
                    {d.name || label} <span className="text-muted-brand font-normal">· {label}</span>
                  </p>
                  <p className="text-xs text-muted-brand">
                    {d.gender ? d.gender[0].toUpperCase() + d.gender.slice(1) : "-"} · {age ?? "-"} yrs
                  </p>
                </div>
                <p className="text-xs text-secondary-brand">
                  DOB: {d.dob || "-"} · Height: {d.heightCm || "-"} cm · Weight: {d.weightKg || "-"} kg
                </p>
                {pe.hasCondition && (
                  <p className="text-xs text-secondary-brand mt-1">
                    Pre-existing: {pe.condition} (since {pe.since})
                    {pe.onMedication && ` · On ${pe.medicineName}, ${pe.dosesPerDay}/day`}
                  </p>
                )}
                <p className="text-xs text-muted-brand mt-1">
                  Drinking: {ls.drinking?.status === "yes" ? `Yes (${ls.drinking.since}, ${ls.drinking.quantity})` : "No"}
                  {" · "}
                  Smoking: {ls.smoking?.status === "yes" ? `Yes (${ls.smoking.since}, ${ls.smoking.sticksPerDay}/day)` : "No"}
                  {" · "}
                  Chewing: {ls.chewing?.status === "yes" ? `Yes (${ls.chewing.since}, ${ls.chewing.product})` : "No"}
                </p>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
